import type { ISelectItem } from '~/types/models'
import type { TEnterableTerm } from '~/types/msService/models'

export interface IGetRecursMrcAmount {
  unitRcAmount: number | undefined
  recurs: ISelectItem | undefined
  term: TEnterableTerm | undefined
}

// Hoisted to module scope deliberately. This used to be constructed inside
// getRecursMrcAmount, which runs once per msService on every setUpMSServices
// sweep — three sweeps per silo overview display — so a large company rebuilt
// this Map thousands of times to answer a single lookup. Same class
// of fix as cynqueModules/getNumberFormat.ts.
//
// Safe to share: it is read-only (`.has` / `.get`) and keyed by the recurs
// LABEL, which is stable select-list data, not per-entity state.
const RECURS_DIVISOR_BY_LABEL = new Map([
  ['Monthly', 1],
  ['Annually', 12],
  ['1x', 0],
  ['N/A', 0],
])

// An annual charge on a term shorter than a year is spread over the term's
// months, not twelve. A 1-month term, or no numeric term at all, keeps 12.
function getRecursDivisor(
  recursLabel: string,
  term: TEnterableTerm | undefined
): number | undefined {
  const termMonths = Number(term)
  if (recursLabel === 'Annually' && termMonths > 1 && termMonths < 12) {
    return termMonths
  }
  return RECURS_DIVISOR_BY_LABEL.get(recursLabel)
}

export function getRecursMrcAmount(o: IGetRecursMrcAmount) {
  const { unitRcAmount, recurs, term } = o
  if (!recurs) {
    return 0
  }
  if (!RECURS_DIVISOR_BY_LABEL.has(recurs.label)) {
    throw new Error(`recursMap does not contain recurs.label '${recurs.label}'`)
  }
  const divisor = getRecursDivisor(recurs.label, term)
  return !divisor || !unitRcAmount ? 0 : unitRcAmount / divisor
}

export interface IGetRecursPaidDescr {
  recurs: ISelectItem | undefined
  paid: ISelectItem | undefined
}

export function getRecursPaidDescr(o: IGetRecursPaidDescr): string {
  const { recurs, paid } = o
  return recurs && recurs.label !== 'N/A' && paid && paid.label !== 'N/A'
    ? `${Array.from(recurs.label)[0]} / ${Array.from(paid.label)[0]}`
    : '--'
}
